import React from 'react';
import styled, { css } from 'styled-components';
import { Message, MessageSenderType, MessageDirection } from '../../models/message';
import { useTheme } from '@mui/material/styles';

interface ChatMessageProps {
    message: Message;
    showTranslation?: boolean;
}

const getSenderNameText = (senderType: MessageSenderType): string => {
    switch (senderType) {
        case MessageSenderType.AI:
            return 'AI Bot';
        case MessageSenderType.ADMIN:
            return 'Admin'; // This name won't usually be shown for admin's own messages
        case MessageSenderType.USER:
            return 'Worker';
        case MessageSenderType.SYSTEM:
            return 'System';
        default:
            return 'Unknown';
    }
};

interface MessageBubbleProps {
    $senderType: MessageSenderType;
    $direction: MessageDirection;
    $isDarkMode?: boolean;
}

const MessageBubbleContainer = styled.div<MessageBubbleProps>`
    display: flex;
    margin-bottom: 10px;
    max-width: 75%; // Max width of the bubble container
    align-self: flex-start; // Default to left

    /* AI and Admin messages align to the right */
    ${({ $senderType }) =>
        ($senderType === MessageSenderType.AI || $senderType === MessageSenderType.ADMIN) &&
        css`
            align-self: flex-end;
        `}

    /* Worker (USER) messages align to the left (already default, but explicit) */
    ${({ $senderType, $direction }) =>
        $senderType === MessageSenderType.USER && $direction === MessageDirection.INCOMING && // Worker's incoming message
        css`
            align-self: flex-start;
        `}
    
    /* System messages align to center */
    ${({ $senderType }) =>
        $senderType === MessageSenderType.SYSTEM &&
        css`
            align-self: center;
            max-width: 100%; /* System messages can be wider */
        `}
`;

const Bubble = styled.div<MessageBubbleProps>`
    padding: 10px 15px;
    border-radius: 18px;
    word-wrap: break-word;
    position: relative;
    color: ${props => props.$isDarkMode ? '#F1F5F9' : '#0F172A'};
    box-shadow: 0 1px 1px rgba(0, 0, 0, ${props => props.$isDarkMode ? 0.3 : 0.06});

    /* Default bubble style (Worker messages - left) */
    background-color: ${props => props.$isDarkMode ? '#1E293B' : '#FFFFFF'};
    border-bottom-left-radius: 8px; // Tail effect

    /* AI Messages (Right) */
    ${({ $senderType, $isDarkMode }) =>
        $senderType === MessageSenderType.AI &&
        css`
            background-color: ${$isDarkMode ? '#1E3A5F' : '#EFF6FF'};
            color: ${$isDarkMode ? '#93C5FD' : '#1E40AF'};
            border-bottom-right-radius: 8px;
            border-bottom-left-radius: 18px; // Reset other tail
        `}

    /* Admin Messages (Right) */
    ${({ $senderType, $isDarkMode }) =>
        $senderType === MessageSenderType.ADMIN &&
        css`
            background-color: ${$isDarkMode ? '#14532D' : '#DCFCE7'};
            color: ${$isDarkMode ? '#86EFAC' : '#166534'};
            border-bottom-right-radius: 8px;
            border-bottom-left-radius: 18px; // Reset other tail
        `}
    
    /* System Messages (Center) */
    ${({ $senderType, $isDarkMode }) =>
        $senderType === MessageSenderType.SYSTEM &&
        css`
            background-color: ${$isDarkMode ? '#111827' : '#F1F5F9'};
            color: ${$isDarkMode ? '#94A3B8' : '#64748B'};
            font-style: italic;
            font-size: 0.85rem;
            text-align: center;
            border-radius: 8px; // System messages might not need a tail
        `}
`;

const MessageContent = styled.p`
    margin: 0;
    padding-bottom: 18px; /* Space for timestamp */
    white-space: pre-wrap;
`;

const Timestamp = styled.span`
    font-size: 0.7rem;
    color: #8696a0; 
    position: absolute;
    bottom: 5px;
    right: 12px;
`;

const SenderName = styled.span`
    font-size: 0.8rem;
    font-weight: bold;
    display: block;
    margin-bottom: 3px;
    color: #2563EB;
`;

const AdminUsernameDisplay = styled(SenderName)`
    color: #64748B;
`;

const TranslationIndicator = styled.span<{ $isDarkMode?: boolean }>`
    font-size: 0.65rem;
    color: ${props => props.$isDarkMode ? '#60A5FA' : '#2563EB'};
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-left: 6px;
    opacity: 0.8;
`;

const ChatMessage: React.FC<ChatMessageProps> = ({ message, showTranslation = false }) => {
    const { senderType, direction, receivedAt, message: messageText, adminUsername, translatedMessage, deliveryStatus } = message;
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    const time = new Date(receivedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    // Determine which text to display
    const displayText = showTranslation && translatedMessage ? translatedMessage : messageText;
    const isShowingTranslation = showTranslation && translatedMessage;
    const deliveryLabel = direction === MessageDirection.OUTGOING && deliveryStatus
        ? deliveryStatus.toLowerCase()
        : null;

    // Determine if sender name should be shown for non-admin messages
    const showStandardSenderName =
        (senderType === MessageSenderType.AI) ||
        (senderType === MessageSenderType.USER && direction === MessageDirection.INCOMING);

    return (
        <MessageBubbleContainer $senderType={senderType} $direction={direction}>
            <Bubble $senderType={senderType} $direction={direction} $isDarkMode={isDarkMode}>
                {/* Display Admin Username if present and message is from ADMIN */}
                {senderType === MessageSenderType.ADMIN && adminUsername &&
                    <AdminUsernameDisplay>
                        {adminUsername}
                    </AdminUsernameDisplay>
                }
                {/* Display standard sender name for AI or incoming User messages */}
                {showStandardSenderName &&
                    <SenderName style={{ color: senderType === MessageSenderType.AI ? '#16A34A' : '#2563EB' }}>
                        {getSenderNameText(senderType)}
                        {isShowingTranslation && (
                            <TranslationIndicator $isDarkMode={isDarkMode}>
                                🌐 translated
                            </TranslationIndicator>
                        )}
                    </SenderName>
                }
                <MessageContent>{displayText}</MessageContent>
                <Timestamp>{time}{deliveryLabel ? ` · ${deliveryLabel}` : ''}</Timestamp>
            </Bubble>
        </MessageBubbleContainer>
    );
};

export default ChatMessage;
